import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "@/lib/api";

const POLL_INTERVAL_MS = 60_000;
const TYPE_ICONS = {
    badge_unlocked: "🏆",
    badge_earned: "🏆",
    campaign_completed: "📬",
    marketplace_purchase: "🛍️",
    plan_renewed: "💳",
    referral_converted: "🎯",
    admin_announcement: "📣",
    data_export_ready: "📦",
    blog_published: "📝",
    // Phase 22b community events
    comment_reply: "💬",
    dm_received: "✉️",
    group_join_requested: "👥",
    group_join_approved: "✅",
    group_invite: "📨",
    event_rsvp: "📅",
    event_reminder: "⏰",
    room_role_changed: "🎭",
    system: "⚙️",
};

function formatRelative(iso) {
    if (!iso) return "";
    const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
    return `${Math.floor(secs / 86400)}d`;
}

/**
 * Notification bell — Phase 6B.
 *
 * Polls unread count every 60s. Click opens dropdown with the last 20
 * items + mark-all-read + link to /notifications.
 */
export default function NotificationBell() {
    const nav = useNavigate();
    const { t } = useTranslation();
    const [count, setCount] = useState(0);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    // Polling — silent, cheap endpoint.
    // Phase 22b: prefer /notifications/unread-count (kill-switch aware);
    // fallback to legacy /notifications/count if the 22b router returns 503.
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await apiFetch("/notifications/unread-count");
                if (mounted) setCount(res.unread_count || 0);
            } catch {
                try {
                    const res = await apiFetch("/notifications/count");
                    if (mounted) setCount(res.unread_count || 0);
                } catch {}
            }
        };
        load();
        const t = setInterval(load, POLL_INTERVAL_MS);
        return () => {
            mounted = false;
            clearInterval(t);
        };
    }, []);

    // Close on outside click
    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const openDropdown = async () => {
        setOpen((o) => !o);
        if (open || items.length) return;
        setLoading(true);
        try {
            const res = await apiFetch("/notifications?limit=10");
            setItems(res.items || []);
        } catch {} finally {
            setLoading(false);
        }
    };

    const markAllRead = async () => {
        try {
            // Phase 22b endpoint (kill-switch aware); fallback to legacy Phase 6B.
            try {
                await apiFetch("/notifications/mark-all-seen", { method: "POST" });
            } catch {
                await apiFetch("/notifications/read-all", { method: "POST" });
            }
            setItems((prev) => prev.map((n) => ({ ...n, is_read: true, seen: true })));
            setCount(0);
        } catch {}
    };

    const clickItem = async (n) => {
        try {
            const already = n.is_read || n.seen;
            if (!already) {
                // Phase 22b endpoint; fallback to legacy Phase 6B.
                try {
                    await apiFetch(`/notifications/${n.id}/mark-seen`, { method: "POST" });
                } catch {
                    await apiFetch(`/notifications/${n.id}/read`, { method: "POST" });
                }
                setCount((c) => Math.max(0, c - 1));
            }
        } catch {}
        setOpen(false);
        if (n.action_url) {
            if (n.action_url.startsWith("/")) nav(n.action_url);
            else window.location.href = n.action_url;
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                data-testid="notification-bell"
                onClick={openDropdown}
                className="relative flex items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-card)] h-8 w-8 hover:border-[var(--color-primary)]/50 transition-colors"
                aria-label="Notifications"
            >
                <Bell className="h-3.5 w-3.5 text-text-secondary" />
                {count > 0 && (
                    <span
                        data-testid="notification-count-badge"
                        className="absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full bg-[var(--color-alert)] text-white text-[9px] font-medium grid place-items-center px-1"
                    >
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notification-dropdown"
                    className="absolute right-0 mt-2 w-80 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden z-50"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
                        <span className="text-xs uppercase tracking-widest text-text-secondary">
                            {t("notifications.title")}
                        </span>
                        {count > 0 && (
                            <button
                                onClick={markAllRead}
                                data-testid="notification-mark-all-read"
                                className="text-[10px] text-text-secondary hover:text-text-main flex items-center gap-1"
                            >
                                <CheckCheck className="h-3 w-3" />
                                {t("notifications.mark_all_read")}
                            </button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {loading && (
                            <div className="text-xs text-text-secondary text-center py-6">Loading…</div>
                        )}
                        {!loading && items.length === 0 && (
                            <div
                                data-testid="notification-dropdown-empty"
                                className="text-xs text-text-secondary text-center py-8 px-4"
                            >
                                {t("notifications.empty")}
                            </div>
                        )}
                        {items.map((n) => {
                            const isSeen = n.is_read || n.seen;
                            return (
                            <button
                                key={n.id}
                                data-testid={`notification-item-${n.id}`}
                                onClick={() => clickItem(n)}
                                className={`w-full text-left px-4 py-3 border-b border-[var(--color-border)]/50 hover:bg-[var(--color-panel-ai)] transition-colors ${
                                    isSeen ? "opacity-70" : ""
                                }`}
                            >
                                <div className="flex gap-3 items-start">
                                    <span className="text-lg leading-none pt-0.5">
                                        {TYPE_ICONS[n.type] || TYPE_ICONS[n.event_type] || "🔔"}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs font-medium truncate">
                                                {n.title || n.type || n.event_type}
                                            </span>
                                            {!isSeen && (
                                                <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)] shrink-0" />
                                            )}
                                        </div>
                                        <p className="text-[11px] text-text-secondary line-clamp-2 mt-0.5">
                                            {n.body || n.summary}
                                        </p>
                                        <span className="text-[10px] text-text-secondary/80">
                                            {formatRelative(n.created_at)}
                                        </span>
                                    </div>
                                </div>
                            </button>
                            );
                        })}
                    </div>

                    <Link
                        to="/notifications"
                        onClick={() => setOpen(false)}
                        data-testid="notification-see-all"
                        className="block text-center py-2.5 border-t border-[var(--color-border)] text-xs text-text-secondary hover:text-text-main"
                    >
                        {t("notifications.see_all")}
                    </Link>
                </div>
            )}
        </div>
    );
}
